import {useCallback, useEffect, useLayoutEffect, useRef} from 'react';

import {getDatadogRum} from './datadogRumFacade';
// Type-only import, so no runtime cycle with workspaceLoading.ts.
import {type WorkspaceLoadingVital} from './workspace/workspaceLoading';

const datadogRumViewTimings = new Set<string>();
const datadogRumViewTimingTimestamps = new Map<string, number>();

// performance.now() offset for the current RUM view start. This starts at 0
// because the SDK backdates the first manually-tracked view to navigation
// start, so the first view begins at performance.timeOrigin.
let rumViewStartOffsetMs = 0;
let hasAnchoredInitialRumViewStart = false;

/**
 * All custom timing names sent to Datadog RUM via `useFirstRumTiming`.
 * Names must be snake_case: timing names are used
 * as facet paths (`@view.custom_timings.<name>`), so a `.` would create a
 * nested attribute instead of a flat timing.
 */
export type DatadogRUMTimings =
  // Workspace loading stage names are also sent as custom timings.
  | WorkspaceLoadingVital
  // existing timings we are trying to deprecate
  | 'run_selector_initial_loaded'
  | 'workspace_no_open_sections'
  | 'workspace_visible'
  // Emitted by useCustomerReadyWhen, suffixed with the page's PageViews name
  | `perf_customer_ready-${string}`;

export type UseFirstRumTimingOptions = {
  /**
   * When to record the timing:
   * - `'mount'`: on first mount, at layout-effect time (the DOM is committed,
   *   but the browser has not painted yet). Use this only when that exact
   *   pre-paint stage is the measurement you want.
   * - a boolean: checked after every render (passive effect, post-paint);
   *   records on the first render where it's true.
   * - a predicate: evaluated after every render until the timing has been
   *   recorded for the current view; records when it returns true.
   * - `'manual'`: never fires automatically; call the returned trigger.
   */
  when: 'mount' | 'manual' | boolean | (() => boolean);
};

/**
 * Clears per-view timing state and anchors timing helpers to a new RUM view.
 * Called by `startDatadogRumView`.
 */
export function resetRumViewTimings() {
  datadogRumViewTimings.clear();
  datadogRumViewTimingTimestamps.clear();

  // First view: keep the navigation-start timestamp.
  if (hasAnchoredInitialRumViewStart) {
    rumViewStartOffsetMs = performance.now();
  } else {
    hasAnchoredInitialRumViewStart = true;
  }
}

/**
 * Returns the current RUM view start as an epoch timestamp in milliseconds.
 */
export function getRumViewStartTimestamp(): number {
  return performance.timeOrigin + rumViewStartOffsetMs;
}

/**
 * Returns the set of timing names that have been recorded in the current view.
 * This is used to track which timings have already been logged.
 *
 * @returns The set of recorded timing names
 */
export function getDatadogRumViewTimings() {
  return datadogRumViewTimings;
}

/**
 * Returns epoch timestamps associated with timings in the current view.
 * This is cleared with the first-timing ledger when a new view starts.
 *
 * @returns The map of timing names to epoch timestamps
 */
export function getDatadogRumViewTimingTimestamps() {
  return datadogRumViewTimingTimestamps;
}

/**
 * Checks whether a timing has already been recorded in the current RUM view.
 *
 * @param name - The timing name to check
 * @returns True if the timing has been recorded, false otherwise
 */
export function hasRecordedRumTiming(name: string) {
  return getDatadogRumViewTimings().has(name);
}

/**
 * Records the first instance of a timing in the current RUM view. Later calls
 * with the same name are ignored until `resetRumViewTimings` clears the view
 * ledger for the next route.
 *
 * Prefer `useFirstRumTiming` from React components unless a specialized
 * lifecycle wrapper needs to supply an explicit timestamp.
 *
 * @param name - The timing name
 * @param time - Optional epoch or relative timestamp accepted by Datadog
 * @returns True if the timing was recorded, false if it deduped to a no-op
 */
export function recordFirstRumTiming(name: DatadogRUMTimings, time?: number) {
  if (hasRecordedRumTiming(name)) {
    return false;
  }

  getDatadogRumViewTimings().add(name);
  getDatadogRum().addTiming(name, time);
  return true;
}

export function useFirstTimingWhen(
  metric: DatadogRUMTimings,
  when: UseFirstRumTimingOptions['when'],
  record: () => void
) {
  // Ref so a changing `when` value can't retrigger the mount effect.
  const whenRef = useRef(when);
  // oxlint-disable-next-line react/refs
  whenRef.current = when;

  useLayoutEffect(() => {
    if (whenRef.current === 'mount') {
      record();
    }
  }, [record]);

  // Boolean/predicate conditions can become true on any render. Re-check after
  // every render; the recorder dedupes recorded timings.
  useEffect(() => {
    const currentWhen = whenRef.current;
    if (currentWhen === true) {
      record();
    } else if (
      typeof currentWhen === 'function' &&
      !hasRecordedRumTiming(metric) &&
      currentWhen()
    ) {
      record();
    }
  });

  return record;
}

/**
 * The single wrapper around Datadog RUM's `addTiming`. Records only the
 * *first* trigger of each metric per RUM view (see `recordFirstRumTiming`) — no
 * matter how many components use it or how often the trigger fires, later
 * triggers are no-ops until a new view starts.
 *
 * @example
 * ```ts
 * // Record when this component first mounts (pre-paint):
 * useFirstRumTiming('my_component_mounted', {when: 'mount'});
 *
 * // Record when a condition first becomes true:
 * const {data, loading} = useQuery(...);
 * useFirstRumTiming('my_data_loaded', {when: !loading && !!data});
 *
 * // Record from a callback:
 * const recordScrolled = useFirstRumTiming('my_list_scrolled', {when: 'manual'});
 * <List onScroll={recordScrolled} />
 * ```
 *
 * @param metric - The timing metric name
 * @param options - See {@link UseFirstRumTimingOptions}
 * @returns A callback to record the timing (useful with `when: 'manual'`)
 */
export function useFirstRumTiming(
  metric: DatadogRUMTimings,
  {when}: UseFirstRumTimingOptions
) {
  const record = useCallback(() => {
    recordFirstRumTiming(metric);
  }, [metric]);

  return useFirstTimingWhen(metric, when, record);
}
